import React, { useEffect, useRef, useState } from "react";
import { Button } from "@shared/ui.primitives";

import {
  backButton,
  clearButton,
  errorText,
  inputGroup,
  searchInput,
  wrapper
} from "./SearchBar.css";
import { CloseIcon } from "@shared/ui.icons";

type Props = {
  onChange: (value: string) => void;
  onSubmit: () => void;
  onFocus: () => void;
  onBack: () => void;
  value: string;
  placeholder?: string;
  isSearchOpen?: boolean;
};

const BackIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" focusable="false" width="20px" height="20px">
    <path
      fill="#000"
      d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
    />
  </svg>
);

const MIN_SEARCH_LENGTH = 2;

export const SearchBar: React.FC<Props> = ({
  onChange,
  onSubmit,
  onFocus,
  onBack,
  value,
  isSearchOpen,
  placeholder = ""
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (isSearchOpen) inputRef.current?.focus();
  }, [isSearchOpen]);

  return (
    <div className={wrapper}>
      <div className={inputGroup}>
        <button
          type="submit"
          className={backButton}
          aria-label="Back Icon"
          onClick={() => {
            onBack();
          }}
        >
          <BackIcon />
        </button>
        <input
          ref={inputRef}
          type="text"
          name="search-term"
          aria-label="Search"
          className={searchInput}
          data-testid="search-input"
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          value={value}
          onChange={(e) => {
            setIsError(false);
            onChange(e.target.value);
          }}
          onFocus={onFocus}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;

            e.preventDefault();
            e.currentTarget.blur();

            if (value.length < MIN_SEARCH_LENGTH) {
              return setIsError(true);
            }

            onSubmit();
          }}
        />

        {!!value.length && (
          <button
            type="submit"
            className={clearButton}
            onClick={() => {
              setIsError(false);
              onChange("");
              inputRef.current?.focus();
            }}
            aria-label="Clear Icon"
          >
            <CloseIcon width={24} height={24} />
          </button>
        )}
      </div>
      <Button
        text="Search"
        variant="primary"
        width="minContent"
        onClick={() => {
          if (value.length < MIN_SEARCH_LENGTH) {
            return setIsError(true);
          }

          onSubmit();
        }}
      />

      {isError && (
        <p className={errorText}>Please search using at least 2 characters.</p>
      )}
    </div>
  );
};
